import { gql } from '@apollo/client';
import { Checkbox, FormControl, FormLabel, Text, useToast } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { BatteryPowerManagement } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import {
  ConfigDisplayBatteryManagement_ConfigFragment,
  useConfigUpdateBatteryPowerManagementMutation,
} from './__generated__/ConfigDisplayBatteryManagement.graphql';

interface Props {
  config: ConfigDisplayBatteryManagement_ConfigFragment;
  isDisabled?: boolean;
}

export function ConfigDisplayBatteryManagement({ config, isDisabled = false }: Props) {
  const currentPowerManagement = useMemo(
    () => config?.battery?.reported?.powerManagement,
    [config],
  );
  const { t } = useTranslation();
  const toast = useToast();
  const [updateBatteryPowerManagement, { loading }] =
    useConfigUpdateBatteryPowerManagementMutation();

  const handleUpdate = useCallback(
    async (value: boolean) => {
      try {
        await updateBatteryPowerManagement({
          variables: {
            input: {
              configurationId: config.id,
              powerManagement: value ? BatteryPowerManagement.On : BatteryPowerManagement.Off,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('batteryManagementErr'),
          description: fromError(err, 'UpdateBatteryPowerManagement'),
        });
      }
    },
    [config, toast, updateBatteryPowerManagement, t],
  );

  return (
    <FormControl>
      <FormLabel>
        {t('batteryManagement')}
        <FormLabelPendingIndicator isPending={loading} />
      </FormLabel>

      <Text color="gray.500" mb="2">
        {t('batteryManagementText')}
      </Text>
      <Checkbox
        isChecked={currentPowerManagement === BatteryPowerManagement.On}
        onChange={(e) => handleUpdate(e.target.checked)}
        isDisabled={isDisabled}
      >
        {t('enableBatteryManagement')}
      </Checkbox>
    </FormControl>
  );
}

ConfigDisplayBatteryManagement.graphql = {
  fragments: {
    ConfigDisplayBatteryManagement_config: gql`
      fragment ConfigDisplayBatteryManagement_config on ConfigurationModel {
        id
        battery {
          reported {
            powerManagement
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateBatteryPowerManagement: gql`
      mutation ConfigUpdateBatteryPowerManagement(
        $input: ConfigurationUpdateBatteryPowerManagementInput!
      ) {
        configurationUpdateBatteryPowerManagement(input: $input) {
          id
          ...ConfigDisplayBatteryManagement_config
        }
      }
    `,
  },
};
